import React, { useEffect, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiDownload,
  FiEye,
  FiPlay,
  FiRefreshCw,
  FiUploadCloud,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "../../components/sidebar/Sidebar";
import ResponsiveRows from "../../components/ResponsiveRows";
import UserLoginData from "../Auth/UserLoginData";
import { useListHook } from "../Admin/List/hook";
import {
  getBulkDownloadUrl,
  listBulkUploadsApi,
  startBulkUploadProcessingApi,
} from "./api";

const formatCreatedDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const BulkUploadHistoryScreen = () => {
  const { isLoggedIn } = UserLoginData();
  const role = isLoggedIn?.response?.role || "admin";
  const { clientList = [] } = useListHook();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClientId = role === "admin" ? searchParams.get("clientId") || "all" : "all";

  const [uploads, setUploads] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [startingUploadId, setStartingUploadId] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 12 });
  const hasLoadedUploads = useRef(false);

  useEffect(() => {
    if (role !== "admin" || searchParams.has("clientId")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("clientId", "all");
    setSearchParams(nextParams, { replace: true });
  }, [role, searchParams, setSearchParams]);

  useEffect(() => {
    let isCurrentRequest = true;
    const requestClientId = role === "client"
      ? isLoggedIn?.response?._id
      : role === "operational"
        ? (isLoggedIn?.response?.clients || [])
        : selectedClientId;

    setLoading(!hasLoadedUploads.current);
    setLoadError("");
    const loadUploads = async () => {
      try {
        const [data, error] = await listBulkUploadsApi(
          requestClientId,
          role,
          pagination.pageIndex,
          pagination.pageSize
        );
        if (!isCurrentRequest) return;
        if (error) {
          const message = error.response?.data?.message || "Could not load uploads.";
          setUploads([]);
          setRowCount(0);
          setLoadError(message);
          toast.error(message);
        } else {
          setUploads((current) => (data?.uploads || []).map((upload) => {
            const previousUpload = current.find((item) => item._id === upload._id);
            return previousUpload?.status === "PROCESSING" && upload.status === "UPLOADED"
              ? { ...upload, status: "PROCESSING" }
              : upload;
          }));
          setRowCount(data?.count || 0);
        }
      } catch {
        if (!isCurrentRequest) return;
        setUploads([]);
        setRowCount(0);
        setLoadError("Could not load uploads.");
      } finally {
        if (isCurrentRequest) {
          hasLoadedUploads.current = true;
          setLoading(false);
        }
      }
    };

    loadUploads();
    return () => {
      isCurrentRequest = false;
    };
  }, [isLoggedIn, pagination.pageIndex, pagination.pageSize, refreshVersion, role, selectedClientId]);

  const hasProcessingUploads = uploads.some((upload) => upload.status === "PROCESSING");
  useEffect(() => {
    if (!hasProcessingUploads) return undefined;
    const interval = setInterval(() => setRefreshVersion((version) => version + 1), 5000);
    return () => clearInterval(interval);
  }, [hasProcessingUploads]);

  const handleClientChange = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("clientId", event.target.value);
    setSearchParams(nextParams, { replace: true });
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const handleStartProcessing = async (uploadId) => {
    if (startingUploadId) return;
    setStartingUploadId(uploadId);
    try {
      const [data, error] = await startBulkUploadProcessingApi(
        uploadId,
        isLoggedIn?.response?._id
      );
      if (error) {
        toast.error(error.response?.data?.message || "Could not start processing.");
        return;
      }
      setUploads((current) => current.map((upload) =>
        upload._id === uploadId ? { ...upload, status: "PROCESSING" } : upload
      ));
      setRefreshVersion((version) => version + 1);
      toast.success(data?.message || "Bulk processing started.");
    } catch {
      toast.error("Could not start processing.");
    } finally {
      setStartingUploadId("");
    }
  };

  const openUpload = (upload) => {
    const params = new URLSearchParams();
    params.set("uploadId", upload._id);
    params.set("clientId", upload.clientId || selectedClientId);
    navigate(`/bulk-upload?${params.toString()}`, { state: { upload } });
  };

  const columns = [
    {
      accessorKey: "fileName",
      header: "File",
      cell: ({ getValue }) => <span className="bulk-history-filename">{getValue() || "Untitled upload"}</span>,
    },
    { accessorKey: "clientName", header: "Client", cell: ({ getValue }) => getValue() || "-" },
    {
      accessorKey: "createdAt",
      header: "Uploaded",
      cell: ({ getValue }) => formatCreatedDate(getValue()),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="bulk-history-status-cell">
          <span className="bulk-status">{row.original.status}</span>
          {row.original.errorMessage && (
            <small className="bulk-upload-error" title={row.original.errorMessage}>
              {row.original.errorMessage}
            </small>
          )}
        </div>
      ),
    },
    {
      id: "processed",
      header: "Processed",
      cell: ({ row }) => `${Number(row.original.processedRows || 0).toLocaleString()} / ${Number(row.original.totalRows || 0).toLocaleString()}`,
    },
    {
      accessorKey: "successRows",
      header: "Success",
      cell: ({ getValue }) => Number(getValue() || 0).toLocaleString(),
    },
    {
      accessorKey: "failedRows",
      header: "Failed",
      cell: ({ getValue }) => Number(getValue() || 0).toLocaleString(),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const upload = row.original;
        return (
          <div className="bulk-history-actions">
            <button
              type="button"
              className="bulk-secondary-button"
              onClick={() => openUpload(upload)}
              aria-label={`Open ${upload.fileName || "upload"}`}
              title="Open preview"
            >
              <FiEye aria-hidden="true" />
            </button>
            {upload.status !== "PROCESSING" && upload.status !== "COMPLETED" && (
              <button
                type="button"
                className="bulk-primary-button bulk-small-button"
                onClick={() => handleStartProcessing(upload._id)}
                disabled={Boolean(startingUploadId)}
                aria-label={`Begin processing ${upload.fileName || "upload"}`}
                title="Begin processing"
              >
                <FiPlay aria-hidden="true" />
              </button>
            )}
            {upload.status === "COMPLETED" && (
              <a
                className="bulk-download-button"
                href={getBulkDownloadUrl(upload._id)}
                aria-label={`Download ${upload.fileName || "upload"}`}
                title="Download processed file"
              >
                <FiDownload aria-hidden="true" />
              </a>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: uploads,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount,
    getRowId: (row) => row._id,
    getCoreRowModel: getCoreRowModel(),
  });
  const rows = table.getRowModel().rows;
  const firstRow = rowCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const lastRow = Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount);

  return (
    <SideBar>
      <section className="shortlinks-page" aria-labelledby="bulk-history-title">
        <header className="shortlinks-page-header bulk-history-header">
          <div className="bulk-page-intro">
            <span className="create-page-icon" aria-hidden="true"><FiUploadCloud /></span>
            <div>
              <h1 id="bulk-history-title">Upload history</h1>
              <p>Browse processed files and upload results.</p>
            </div>
          </div>
          <div className="bulk-history-header-actions">
            <button
              type="button"
              className="bulk-secondary-button"
              onClick={() => setRefreshVersion((version) => version + 1)}
              disabled={loading}
            >
              <FiRefreshCw aria-hidden="true" />
              Refresh
            </button>
            <button
              type="button"
              className="bulk-primary-button"
              onClick={() => navigate(`/bulk-upload${selectedClientId !== "all" ? `?clientId=${encodeURIComponent(selectedClientId)}` : ""}`)}
            >
              <FiArrowLeft aria-hidden="true" />
              Back to upload
            </button>
          </div>
        </header>

        {role === "admin" && (
          <div className="shortlinks-toolbar bulk-history-toolbar">
            <label htmlFor="bulk-history-client">Client</label>
            <select id="bulk-history-client" value={selectedClientId} onChange={handleClientChange}>
              <option value="all">All clients</option>
              {clientList.map((client) => (
                <option key={client._id} value={client._id}>{client.username}</option>
              ))}
            </select>
          </div>
        )}

        <div className="shortlinks-table-shell">
          <ResponsiveRows
            rows={loading || loadError ? [] : rows}
            getRowId={(row) => row.id}
            resetKey={`${selectedClientId}-${pagination.pageIndex}-${pagination.pageSize}`}
            renderSummary={({ original: upload }) => <><strong>{upload.fileName || "Untitled upload"}</strong><span>{upload.status} · {upload.processedRows || 0} / {upload.totalRows || 0} processed</span></>}
            renderDetails={(row) => (
              <dl>{row.getVisibleCells().map((cell) => (
                <div key={cell.id}>
                  <dt>{cell.column.columnDef.header}</dt>
                  <dd>{flexRender(cell.column.columnDef.cell, cell.getContext())}</dd>
                </div>
              ))}</dl>
            )}
          >
          <div className="shortlinks-table-scroll">
            <table className="shortlinks-table bulk-history-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} scope="col">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="shortlinks-table-message" colSpan={columns.length} role="status">Loading uploads...</td></tr>
                ) : loadError ? (
                  <tr><td className="shortlinks-table-message" colSpan={columns.length} role="alert">{loadError}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className="shortlinks-table-message" colSpan={columns.length}>No uploads found.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </ResponsiveRows>
          <footer className="shortlinks-pagination">
            <div className="shortlinks-pagination-summary">
              <label htmlFor="bulk-history-page-size">Rows per page</label>
              <select
                id="bulk-history-page-size"
                value={pagination.pageSize}
                onChange={(event) => table.setPageSize(Number(event.target.value))}
              >
                {[10, 12, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              <span className="shortlinks-row-range">{firstRow}-{lastRow} of {rowCount}</span>
            </div>
            <div className="shortlinks-page-controls" aria-label="Table pagination">
              <button type="button" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} aria-label="First page"><FiChevronsLeft /></button>
              <button type="button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} aria-label="Previous page"><FiChevronLeft /></button>
              <span>Page {pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}</span>
              <button type="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} aria-label="Next page"><FiChevronRight /></button>
              <button type="button" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} aria-label="Last page"><FiChevronsRight /></button>
            </div>
          </footer>
        </div>
      </section>
    </SideBar>
  );
};

export default BulkUploadHistoryScreen;
